import { useState, useEffect, useRef, useCallback } from 'react';

let envConfig = null; // Cache the config

export const useWebSocket = (onMessage, token) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pollingIntervalRef = useRef(null);
  const [config, setConfig] = useState(null);
  const maxReconnectAttempts = 10;
  const reconnectDelay = 3000;

  // Fetch environment config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      if (envConfig) {
        setConfig(envConfig);
        return;
      }

      // Directly use REACT_APP_BACKEND_URL instead of fetching from backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const ws_url = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      
      const directConfig = {
        env: BACKEND_URL.includes('localhost') ? 'development' : 'production',
        ws_url: ws_url,
        api_url: BACKEND_URL,
        features: {
          realtime: true,
          fallback_polling: false
        }
      };
      
      envConfig = directConfig;
      setConfig(directConfig);
      console.log('🔧 WebSocket: Using direct config from REACT_APP_BACKEND_URL:', directConfig);
    };

    fetchConfig();
  }, []);

  const connect = useCallback(() => {
    if (!token || !config) return;

    try {
      const wsUrl = `${config.ws_url}/api/realtime/ws?token=${token}`;
      console.log(`🔌 WebSocket: Connecting to ${config.env} environment: ${wsUrl}`);
      
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`✅ WebSocket connected (${config.env})`);
        reconnectAttempts.current = 0;
        
        // Clear fallback polling if it was running
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);

        ws.pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message:', data);
          
          // Handle different message types
          if (data.type === 'pong') {
            return; // Ignore pong messages
          }
          
          // Play sound if message has sound flag
          if (data.sound) {
            playNotificationSound();
          }
          
          // Call the onMessage callback
          if (onMessage) {
            onMessage(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.log(`⚠️ WebSocket error in ${config.env} environment`);
      };

      ws.onclose = (event) => {
        console.log(`🔌 WebSocket disconnected (${config.env}, code: ${event.code}, reason: ${event.reason || 'Unknown'})`);
        
        // Clear ping interval
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        // Don't reconnect on authentication failures (code 1008)
        if (event.code === 1008) {
          console.log('WebSocket: Authentication failed, not reconnecting');
          return;
        }
        
        // Enable fallback polling for preview environment
        if (config.features?.fallback_polling && !pollingIntervalRef.current) {
          console.log('🔄 WebSocket: Starting fallback polling (preview environment)');
          startFallbackPolling();
        }
        
        // Auto-reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            connect();
          }, delay);
        } else {
          console.log('WebSocket: Max reconnection attempts reached');
          // For preview, start polling as final fallback
          if (config.features?.fallback_polling && !pollingIntervalRef.current) {
            startFallbackPolling();
          }
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      // Start fallback polling for preview if WebSocket fails
      if (config?.features?.fallback_polling && !pollingIntervalRef.current) {
        startFallbackPolling();
      }
    }
  }, [token, config]);

  const startFallbackPolling = useCallback(() => {
    if (!config || pollingIntervalRef.current) return;

    console.log('🔄 Starting fallback polling every 15 seconds');
    pollingIntervalRef.current = setInterval(() => {
      // Trigger a refresh event to poll for updates
      if (onMessage) {
        onMessage({
          type: 'data_refresh',
          data: { refresh: 'leads', timestamp: Date.now() / 1000 },
          sound: false,
          source: 'polling'
        });
      }
    }, 15000); // Poll every 15 seconds
  }, [config, onMessage]);

  useEffect(() => {
    if (config) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [connect, config]);

  return wsRef.current;
};

// Play notification sound
const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for notification sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound - notification ding
    oscillator.frequency.value = 800; // High frequency for ding sound
    gainNode.gain.value = 0.3; // High volume as requested
    oscillator.type = 'sine';
    
    // Play sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
    
    // Second ding
    setTimeout(() => {
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.value = 1000;
      gainNode2.gain.value = 0.3;
      oscillator2.type = 'sine';
      
      oscillator2.start(audioContext.currentTime);
      oscillator2.stop(audioContext.currentTime + 0.15);
    }, 150);
    
    console.log('🔔 Notification sound played');
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};
